import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Edit, CheckCircle2, XCircle, Clock, Zap, Terminal, LayoutTemplate, RefreshCw } from 'lucide-react';
import { useState, useEffect } from 'react';

interface Template {
  _id: string;
  title: string;
  description: string;
  imageUrls: string[];
  enterpriseGradeSolution: boolean;
  premiumLicense: boolean;
  price: number;
  paymentModel: 'one-time-payment' | 'subscription';
  lifetimeUpdates: boolean;
  keyFeatures: string[];
  includedPages: string[];
  zipFile?: string;
  technologyUsed: string[];
  status: 'pending' | 'approved' | 'rejected';
  submissionDate: string;
  reviewDate?: string;
  rejectionReason?: string;
}

export default function TemplateDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [template, setTemplate] = useState<Template | null>(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      const mockData: Template = {
        _id: id || '1',
        title: 'Enterprise Dashboard Pro',
        description: 'Complete dashboard solution for enterprise applications',
        imageUrls: [
          'https://via.placeholder.com/800x500',
          'https://via.placeholder.com/800x500',
          'https://via.placeholder.com/800x500'
        ],
        enterpriseGradeSolution: true,
        premiumLicense: true,
        price: 299.99,
        paymentModel: 'subscription',
        lifetimeUpdates: true,
        keyFeatures: [
          'Role-based access control',
          'Analytics modules',
          'Multi-language support',
          'Advanced data visualization'
        ],
        includedPages: [
          'Dashboard',
          'User Management',
          'Reports',
          'Settings'
        ],
        zipFile: '/templates/enterprise-dashboard.zip',
        technologyUsed: ['React', 'TypeScript', 'TailwindCSS', 'Node.js'],
        status: 'approved',
        submissionDate: '2024-03-15',
        reviewDate: '2024-03-18',
        rejectionReason: ''
      };
      setTemplate(mockData);
    };
    fetchTemplate();
  }, [id]);

  const getStatusColor = (status: Template['status']) => {
    return {
      approved: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      rejected: 'bg-red-100 text-red-700'
    }[status];
  };

  const getPaymentModelText = (paymentModel: Template['paymentModel']) => {
    return {
      'one-time-payment': 'One-time',
      subscription: 'Subscription'
    }[paymentModel];
  };

  if (!template) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-100 rounded w-32"></div>
          <div className="aspect-video bg-gray-100 rounded-xl"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-square bg-gray-100 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-6">
      <button 
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center text-gray-600 hover:text-gray-800 text-sm font-medium"
      >
        <ArrowLeft size={16} className="mr-2" />
        Back to Templates
      </button>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Image Gallery */}
          <div className="space-y-3">
            <div className="aspect-video bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
              <img 
                src={template.imageUrls[0]} 
                alt={template.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {template.imageUrls.slice(1).map((url, index) => (
                <div key={index} className="aspect-[4/3] bg-gray-50 rounded-md overflow-hidden border border-gray-200">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>

          {/* Key Features */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Zap size={20} className="mr-2 text-blue-600" />
              Core Features
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {template.keyFeatures.map((feature, index) => (
                <div key={index} className="p-3 bg-white rounded-lg border border-gray-200 flex items-start">
                  <div className="h-2 w-2 bg-blue-600 rounded-full mt-2 mr-3" />
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Tech Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Terminal size={20} className="mr-2 text-blue-600" />
                Technology Stack
              </h2>
              <div className="flex flex-wrap gap-2">
                {template.technologyUsed.map((tech, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <LayoutTemplate size={20} className="mr-2 text-blue-600" />
                Included Pages
              </h2>
              <div className="grid gap-2">
                {template.includedPages.map((page, index) => (
                  <div key={index} className="p-2.5 bg-white rounded-md border border-gray-200 flex items-center">
                    <div className="h-2 w-2 bg-blue-600 rounded-full mr-3" />
                    <span className="text-sm text-gray-700">{page}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-4">
            {/* Pricing Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    ${template.price.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {getPaymentModelText(template.paymentModel)} License
                  </div>
                </div>
                {template.lifetimeUpdates && (
                  <span className="flex items-center text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                    <RefreshCw size={12} className="mr-1.5" />
                    Lifetime Updates
                  </span>
                )}
              </div>

              <div className="space-y-2">
                <button 
                  className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-md text-sm font-medium transition-colors"
                  disabled={!template.zipFile}
                >
                  <Download size={16} className="mr-2" />
                  Download Package
                </button>
                <button className="w-full flex items-center justify-center border border-blue-600 text-blue-600 hover:bg-blue-50 py-2.5 rounded-md text-sm font-medium transition-colors">
                  <Edit size={16} className="mr-2" />
                  Edit Template
                </button>
              </div>
            </div>

            {/* Metadata */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
              <div>
                <dt className="text-xs font-medium text-gray-500 mb-1.5">Current Status</dt>
                <dd className={`flex items-center text-sm ${getStatusColor(template.status)} px-3 py-1.5 rounded-md`}>
                  {template.status === 'approved' && <CheckCircle2 size={14} className="mr-2" />}
                  {template.status === 'pending' && <Clock size={14} className="mr-2" />}
                  {template.status === 'rejected' && <XCircle size={14} className="mr-2" />}
                  <span className="capitalize">{template.status}</span>
                </dd>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500 mb-1">Enterprise Grade</dt>
                  <dd className="text-sm text-gray-900">
                    {template.enterpriseGradeSolution ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 mb-1">Premium License</dt>
                  <dd className="text-sm text-gray-900">
                    {template.premiumLicense ? 'Included' : '-'}
                  </dd>
                </div>
              </div>

              {template.rejectionReason && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 mb-1">Rejection Reason</dt>
                  <dd className="text-sm text-gray-700">{template.rejectionReason}</dd>
                </div>
              )}

              <div className="pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  Submitted: {new Date(template.submissionDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
                {template.reviewDate && (
                  <div className="text-xs text-gray-500 mt-1">
                    Reviewed: {new Date(template.reviewDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}